import React, {
  ReactNode,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
  useCallback,
} from 'react';
import classNames from 'classnames';
import { Button, Dropdown } from 'ncoded-component-library';
import { DropdownRef } from 'ncoded-component-library/build/components/molecules/Dropdown/Dropdown.component';
import useCallbackRef from 'hooks/useCallbackRef';
import useScrollLock from 'hooks/useScrollLock';
import { NAVIGATION_VARIANTS, NavigationVariants } from 'models/Navigation';
import ChevrondownIcon from 'icons/Chevrondown.icon';

import './FilterActionWrapper.styles.scss';

const MARGIN_TOP = 12;
const PADDING = 48;

const ADDITIONAL_SPACE = PADDING + MARGIN_TOP;

export type FilterActionWrapperProps = {
  className?: string;
  trigerTitle: string;
  children?: React.ReactNode;
  icon?: ReactNode;
  variant?: NavigationVariants;
  renderAsPortal?: boolean;
  portalClassName?: string;
  onClose?: () => void;
};

const FilterActionWrapper: React.ForwardRefRenderFunction<
  DropdownRef,
  FilterActionWrapperProps
> = (props, ref) => {
  const {
    children,
    className,
    trigerTitle,
    icon,
    renderAsPortal,
    portalClassName,
    variant = NAVIGATION_VARIANTS.SECONDARY,
    onClose,
  } = props;

  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [dropdown, dropdownRef] = useCallbackRef<DropdownRef>(null);

  const [buttonHeight, setButtonHeight] = useState(ADDITIONAL_SPACE);

  const classes = classNames(
    'filter-action-wrapper',
    `filter-action-wrapper--${variant}`,
    className,
  );

  useImperativeHandle(ref, () => dropdown, [dropdown]);

  const { lockScroll, unlockScroll } = useScrollLock();

  const updateButtonHeight = useCallback((node?: HTMLButtonElement | null) => {
    const target = node ?? buttonRef.current;

    if (target) {
      setButtonHeight(target.getBoundingClientRect().height + ADDITIONAL_SPACE);
    }
  }, []);

  const setButtonRef = useCallback(
    (node: HTMLButtonElement | null) => {
      buttonRef.current = node;
      updateButtonHeight(node);
    },
    [updateButtonHeight],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => unlockScroll(), []);

  return (
    <Dropdown
      ref={dropdownRef}
      closeWithJS
      className={classes}
      renderAsPortal={renderAsPortal}
      portalClassName={portalClassName}
      trigger={
        <Button
          icon={icon}
          className="filter-action-wrapper__trigger"
          ref={setButtonRef}
        >
          {trigerTitle}
          <ChevrondownIcon className="arrow" />
        </Button>
      }
      onClose={() => {
        unlockScroll();
        onClose?.();
      }}
      onOpen={() => {
        lockScroll();
      }}
    >
      <div className="filter-action-wrapper">
        <div
          className="filter-action-wrapper__content"
          style={{ maxHeight: `calc(50vh - ${buttonHeight}px)` }}
        >
          {children}
        </div>
      </div>
    </Dropdown>
  );
};

export default forwardRef(FilterActionWrapper);
